import { ListingInfo } from '@apeiron/common'
import { CurrencyTokenType, useCurrencyContext } from '@apeiron/currency'
import { MayNull, useWeb3 } from '@apeiron/temp'
import useGetAuctionPrice from '@src/hooks/apeiron/trade/useGetAuctionPrice'
import { useCallback, useEffect, useState } from 'react'

const usePriceCalculation = (listingInfo?: MayNull<ListingInfo>): Hook => {
  const { getExchangeRate } = useCurrencyContext()

  const { getCurrentPrice } = useGetAuctionPrice()

  const { toWei } = useWeb3()

  const [ethPrice, setEthPrice] = useState(0)

  const [usdPrice, setUsdPrice] = useState(0)

  const [weiPrice, setWeiPrice] = useState('0')

  const getPriceInETH = useCallback(
    (listingInfo?: ListingInfo | null): number => {
      if (!listingInfo) {
        return 0
      } else {
        return getCurrentPrice({
          currencyType: listingInfo.currencyType || CurrencyTokenType.Weth,
          duration: listingInfo.duration,
          endPrice: listingInfo.endPrice,
          startDate: listingInfo.startDate,
          startPrice: listingInfo.startPrice
        })
      }
    },
    [getCurrentPrice]
  )

  const getPriceInUSD = useCallback(
    (listingInfo?: ListingInfo | null): number => {
      return getPriceInETH(listingInfo) * getExchangeRate()
    },
    [getExchangeRate, getPriceInETH]
  )

  const getPriceInWei = useCallback(
    (listingInfo?: ListingInfo | null): string => {
      return toWei(getPriceInETH(listingInfo)).toString()
    },
    [getPriceInETH, toWei]
  )

  useEffect(() => {
    if (listingInfo) {
      setEthPrice(getPriceInETH(listingInfo))
      setUsdPrice(getPriceInUSD(listingInfo))
      setWeiPrice(getPriceInWei(listingInfo))
    }
  }, [getPriceInETH, getPriceInUSD, getPriceInWei, listingInfo])

  return {
    ethPrice,
    usdPrice,
    weiPrice,
    getPriceInUSD
  }
}

type Hook = {
  ethPrice: number
  usdPrice: number
  weiPrice: string
  getPriceInUSD: (listingInfo?: ListingInfo | null) => number
}
export default usePriceCalculation
